"use client";

import { useTranslation } from "react-i18next";
import { FooterSection } from "@/components/marketing/FooterSection";

type PrivacySection = {
  id: string;
  headingKey: string;
  paragraphKeys: readonly string[];
  bulletKeys?: readonly string[];
};

const PRIVACY_SECTIONS: readonly PrivacySection[] = [
  {
    id: "scope",
    headingKey: "PRIVACY_SCOPE_HEADING",
    paragraphKeys: ["PRIVACY_SCOPE_TEXT_1", "PRIVACY_SCOPE_TEXT_2"],
  },
  {
    id: "controller",
    headingKey: "PRIVACY_CONTROLLER_HEADING",
    paragraphKeys: ["PRIVACY_CONTROLLER_TEXT_1", "PRIVACY_CONTROLLER_TEXT_2"],
  },
  {
    id: "data-collected",
    headingKey: "PRIVACY_DATA_COLLECTED_HEADING",
    paragraphKeys: ["PRIVACY_DATA_COLLECTED_TEXT_1", "PRIVACY_DATA_COLLECTED_TEXT_2"],
    bulletKeys: [
      "PRIVACY_DATA_COLLECTED_ITEM_1",
      "PRIVACY_DATA_COLLECTED_ITEM_2",
      "PRIVACY_DATA_COLLECTED_ITEM_3",
      "PRIVACY_DATA_COLLECTED_ITEM_4",
      "PRIVACY_DATA_COLLECTED_ITEM_5",
      "PRIVACY_DATA_COLLECTED_ITEM_6",
    ],
  },
  {
    id: "sources",
    headingKey: "PRIVACY_DATA_SOURCES_HEADING",
    paragraphKeys: ["PRIVACY_DATA_SOURCES_TEXT_1", "PRIVACY_DATA_SOURCES_TEXT_2"],
    bulletKeys: [
      "PRIVACY_DATA_SOURCES_ITEM_1",
      "PRIVACY_DATA_SOURCES_ITEM_2",
      "PRIVACY_DATA_SOURCES_ITEM_3",
      "PRIVACY_DATA_SOURCES_ITEM_4",
    ],
  },
  {
    id: "purposes",
    headingKey: "PRIVACY_PURPOSES_HEADING",
    paragraphKeys: ["PRIVACY_PURPOSES_TEXT_1", "PRIVACY_PURPOSES_TEXT_2"],
    bulletKeys: [
      "PRIVACY_PURPOSES_ITEM_1",
      "PRIVACY_PURPOSES_ITEM_2",
      "PRIVACY_PURPOSES_ITEM_3",
      "PRIVACY_PURPOSES_ITEM_4",
      "PRIVACY_PURPOSES_ITEM_5",
      "PRIVACY_PURPOSES_ITEM_6",
      "PRIVACY_PURPOSES_ITEM_7",
      "PRIVACY_PURPOSES_ITEM_8",
    ],
  },
  {
    id: "legal-bases",
    headingKey: "PRIVACY_LEGAL_BASES_HEADING",
    paragraphKeys: ["PRIVACY_LEGAL_BASES_TEXT_1", "PRIVACY_LEGAL_BASES_TEXT_2"],
    bulletKeys: [
      "PRIVACY_LEGAL_BASES_ITEM_1",
      "PRIVACY_LEGAL_BASES_ITEM_2",
      "PRIVACY_LEGAL_BASES_ITEM_3",
      "PRIVACY_LEGAL_BASES_ITEM_4",
    ],
  },
  {
    id: "ai-automation",
    headingKey: "PRIVACY_AI_AUTOMATION_HEADING",
    paragraphKeys: ["PRIVACY_AI_AUTOMATION_TEXT_1", "PRIVACY_AI_AUTOMATION_TEXT_2"],
  },
  {
    id: "cookies",
    headingKey: "PRIVACY_COOKIES_HEADING",
    paragraphKeys: ["PRIVACY_COOKIES_TEXT_1", "PRIVACY_COOKIES_TEXT_2"],
    bulletKeys: [
      "PRIVACY_COOKIES_ITEM_1",
      "PRIVACY_COOKIES_ITEM_2",
      "PRIVACY_COOKIES_ITEM_3",
      "PRIVACY_COOKIES_ITEM_4",
    ],
  },
  {
    id: "sharing",
    headingKey: "PRIVACY_SHARING_DISCLOSURE_HEADING",
    paragraphKeys: ["PRIVACY_SHARING_DISCLOSURE_TEXT_1", "PRIVACY_SHARING_DISCLOSURE_TEXT_2"],
    bulletKeys: [
      "PRIVACY_SHARING_DISCLOSURE_ITEM_1",
      "PRIVACY_SHARING_DISCLOSURE_ITEM_2",
      "PRIVACY_SHARING_DISCLOSURE_ITEM_3",
      "PRIVACY_SHARING_DISCLOSURE_ITEM_4",
      "PRIVACY_SHARING_DISCLOSURE_ITEM_5",
    ],
  },
  {
    id: "transfers",
    headingKey: "PRIVACY_INTERNATIONAL_TRANSFERS_HEADING",
    paragraphKeys: ["PRIVACY_INTERNATIONAL_TRANSFERS_TEXT_1", "PRIVACY_INTERNATIONAL_TRANSFERS_TEXT_2"],
  },
  {
    id: "retention",
    headingKey: "PRIVACY_RETENTION_HEADING",
    paragraphKeys: ["PRIVACY_RETENTION_TEXT_1", "PRIVACY_RETENTION_TEXT_2"],
  },
  {
    id: "security",
    headingKey: "PRIVACY_SECURITY_HEADING",
    paragraphKeys: ["PRIVACY_SECURITY_TEXT_1", "PRIVACY_SECURITY_TEXT_2"],
  },
  {
    id: "rights",
    headingKey: "PRIVACY_RIGHTS_CHOICES_HEADING",
    paragraphKeys: ["PRIVACY_RIGHTS_CHOICES_TEXT_1", "PRIVACY_RIGHTS_CHOICES_TEXT_2"],
    bulletKeys: [
      "PRIVACY_RIGHTS_CHOICES_ITEM_1",
      "PRIVACY_RIGHTS_CHOICES_ITEM_2",
      "PRIVACY_RIGHTS_CHOICES_ITEM_3",
      "PRIVACY_RIGHTS_CHOICES_ITEM_4",
      "PRIVACY_RIGHTS_CHOICES_ITEM_5",
      "PRIVACY_RIGHTS_CHOICES_ITEM_6",
      "PRIVACY_RIGHTS_CHOICES_ITEM_7",
    ],
  },
  {
    id: "regional",
    headingKey: "PRIVACY_REGIONAL_NOTICES_HEADING",
    paragraphKeys: ["PRIVACY_REGIONAL_NOTICES_TEXT_1", "PRIVACY_REGIONAL_NOTICES_TEXT_2"],
  },
  {
    id: "children",
    headingKey: "PRIVACY_CHILDREN_HEADING",
    paragraphKeys: ["PRIVACY_CHILDREN_TEXT_1", "PRIVACY_CHILDREN_TEXT_2"],
  },
  {
    id: "communications",
    headingKey: "PRIVACY_COMMUNICATIONS_HEADING",
    paragraphKeys: ["PRIVACY_COMMUNICATIONS_TEXT_1", "PRIVACY_COMMUNICATIONS_TEXT_2"],
  },
  {
    id: "links",
    headingKey: "PRIVACY_EXTERNAL_LINKS_HEADING",
    paragraphKeys: ["PRIVACY_EXTERNAL_LINKS_TEXT_1", "PRIVACY_EXTERNAL_LINKS_TEXT_2"],
  },
  {
    id: "changes",
    headingKey: "PRIVACY_CHANGES_HEADING",
    paragraphKeys: ["PRIVACY_CHANGES_TEXT_1", "PRIVACY_CHANGES_TEXT_2"],
  },
  {
    id: "contact",
    headingKey: "PRIVACY_CONTACT_HEADING",
    paragraphKeys: ["PRIVACY_CONTACT_TEXT_1", "PRIVACY_CONTACT_TEXT_2"],
  },
];

export default function PrivacyContent() {
  const { t } = useTranslation();

  return (
    <main className="mx-auto max-w-7xl px-5 py-12">
      <h1 className="mb-2 text-2xl font-bold md:text-3xl">{t("PRIVACY_TITLE_TEXT")}</h1>
      <p className="mb-8 text-sm text-muted-foreground">{t("PRIVACY_LAST_UPDATED_TEXT")}</p>

      <section className="mb-8 space-y-4 text-sm leading-6 text-muted-foreground">
        <p>{t("PRIVACY_INTRO_TEXT_1")}</p>
        <p>{t("PRIVACY_INTRO_TEXT_2")}</p>
      </section>

      <nav
        aria-label={t("PRIVACY_TABLE_OF_CONTENTS_ARIA_LABEL")}
        className="mb-10 rounded-xl border bg-card/40 p-4 text-sm"
      >
        <h2 className="mb-3 text-base font-semibold text-foreground">{t("PRIVACY_TABLE_OF_CONTENTS_TITLE_TEXT")}</h2>
        <ol className="grid gap-2 md:grid-cols-2">
          {PRIVACY_SECTIONS.map((section) => (
            <li key={section.id}>
              <a className="text-muted-foreground transition-colors hover:text-foreground" href={`#${section.id}`}>
                {t(section.headingKey)}
              </a>
            </li>
          ))}
        </ol>
      </nav>

      <section className="space-y-7 text-sm leading-6 text-muted-foreground">
        {PRIVACY_SECTIONS.map((section) => (
          <article className="scroll-mt-24 space-y-3" id={section.id} key={section.id}>
            <h2 className="text-lg font-semibold text-foreground">{t(section.headingKey)}</h2>
            {section.paragraphKeys.map((paragraphKey) => (
              <p key={paragraphKey}>{t(paragraphKey)}</p>
            ))}
            {section.bulletKeys ? (
              <ul className="list-disc space-y-1 pl-5">
                {section.bulletKeys.map((bulletKey) => (
                  <li key={bulletKey}>{t(bulletKey)}</li>
                ))}
              </ul>
            ) : null}
          </article>
        ))}
      </section>
      <div className="mt-10">
        <FooterSection />
      </div>
    </main>
  );
}
